import type { FormEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LanguageSwitcher } from '@/components/language-switcher';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { authService } from '@/services/auth.service';
import { useProvider } from '@/hooks/useTenant';
import { useBrandLogo } from '@/hooks/useBrandLogo';
import { useAuthPageTheme } from '@/hooks/useAuthPageTheme';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Separator } from '@/components/ui/separator';
import { useSocialAuth } from '@/plugins/social-auth/useSocialAuth';
import { useAvailableSocialProviders } from '@/plugins/social-auth/useAvailableSocialProviders';
import { Github, Apple, Loader2, Lock, Globe } from 'lucide-react';
import type { OAuthProvider } from '@/types/auth';
import { GoogleIcon } from '@/components/icons/GoogleIcon';
import { MicrosoftIcon } from '@/components/icons/MicrosoftIcon';
import { getCurrentDomain } from '@/utils/domain';
import { usePasswordLoginAvailability } from '@/hooks/auth/usePasswordLoginAvailability';
import { usePublicProviderTermsUrl } from '@/hooks/provider/useProviderTermsSettings';
import { resolveSigninRedirectUrl } from '@/lib/auth/signin-redirect';
import { normalizeEmail } from '@/lib/email';

const SOCIAL_PROVIDER_ORDER: OAuthProvider[] = ['google', 'apple', 'github', 'azure'];
const PROVIDER_LABEL_KEYS: Record<OAuthProvider, string> = {
  google: 'auth.continueWithGoogle',
  apple: 'auth.continueWithApple',
  github: 'auth.continueWithGithub',
  azure: 'auth.continueWithMicrosoft',
};
const PROVIDER_LABEL_FALLBACK: Record<OAuthProvider, string> = {
  google: 'Continue with Google',
  apple: 'Continue with Apple',
  github: 'Continue with GitHub',
  azure: 'Continue with Microsoft',
};

type JsonApiErrorItem = {
  title?: unknown;
  detail?: unknown;
  message?: unknown;
  code?: unknown;
  status?: unknown;
};

type TranslateFn = TFunction;

const EMAIL_VERIFICATION_ERROR_KEY = 'customer.portal.login.errors.emailNotVerified';
const AUTH_ERROR_CODE_TRANSLATIONS: Record<string, string> = {
  EMAIL_NOT_VERIFIED: EMAIL_VERIFICATION_ERROR_KEY,
  EMAIL_VERIFICATION_REQUIRED: EMAIL_VERIFICATION_ERROR_KEY,
  UNVERIFIED_EMAIL: EMAIL_VERIFICATION_ERROR_KEY,
};
const EMAIL_VERIFICATION_PATTERN = /verify.*email|email.*verify/i;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

function readErrorMessageFromData(data: unknown): string | null {
  if (isNonEmptyString(data)) {
    return data;
  }

  if (!data || typeof data !== 'object') {
    return null;
  }

  const record = data as Record<string, unknown>;
  const errors = record.errors;

  if (Array.isArray(errors) && errors.length > 0) {
    const firstError = errors[0] as JsonApiErrorItem;
    if (isNonEmptyString(firstError.detail)) {
      return firstError.detail;
    }
    if (isNonEmptyString(firstError.title)) {
      return firstError.title;
    }
    if (isNonEmptyString(firstError.message)) {
      return firstError.message;
    }
  }

  if (errors && typeof errors === 'object') {
    for (const value of Object.values(errors)) {
      if (Array.isArray(value)) {
        const message = value.find(isNonEmptyString);
        if (message) {
          return message;
        }
      } else if (isNonEmptyString(value)) {
        return value;
      }
    }
  }

  if (isNonEmptyString(record.message)) {
    return record.message;
  }

  if (isNonEmptyString(record.error)) {
    return record.error;
  }

  return null;
}

function readErrorCodeFromData(data: unknown): string | null {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const record = data as Record<string, unknown>;
  const errors = record.errors;

  if (Array.isArray(errors) && errors.length > 0) {
    const firstError = errors[0] as JsonApiErrorItem;
    if (isNonEmptyString(firstError.code)) {
      return firstError.code;
    }
  }

  if (isNonEmptyString(record.code)) {
    return record.code;
  }

  return null;
}

function resolveAuthErrorMessage(
  error: unknown,
  fallback: string,
  translate?: TranslateFn,
): string {
  const status = resolveAuthErrorStatus(error);
  if (!error) {
    return fallback;
  }

  const responseData =
    typeof error === 'object' && error && 'response' in error
      ? (error as { response?: { data?: unknown } }).response?.data
      : undefined;
  const rawMessage = readErrorMessageFromData(responseData) ?? readErrorMessageFromData(error);
  const code = readErrorCodeFromData(responseData) ?? readErrorCodeFromData(error);

  if (translate) {
    if (code && AUTH_ERROR_CODE_TRANSLATIONS[code]) {
      return translate(AUTH_ERROR_CODE_TRANSLATIONS[code], rawMessage ?? fallback);
    }

    if (
      status === 403 &&
      rawMessage &&
      EMAIL_VERIFICATION_PATTERN.test(rawMessage)
    ) {
      return translate(EMAIL_VERIFICATION_ERROR_KEY, rawMessage);
    }
  }

  if (rawMessage) {
    return rawMessage;
  }

  if (typeof error === 'object' && 'response' in error) {
    const message = readErrorMessageFromData(responseData);
    if (message) {
      return message;
    }
  }

  if (error instanceof Error && isNonEmptyString(error.message)) {
    return error.message;
  }

  return fallback;
}

function resolveAuthErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const responseStatus = (error as { response?: { status?: number } }).response?.status;
  if (typeof responseStatus === 'number') {
    return responseStatus;
  }

  const directStatus = (error as { status?: number }).status;
  if (typeof directStatus === 'number') {
    return directStatus;
  }

  return undefined;
}

export default function CustomerLogin() {
  useAuthPageTheme();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, hydrateFromResponse } = useCustomerAuth();
  const provider = useProvider();
  const brandLogo = useBrandLogo();
  const { t } = useTranslation();
  const providerName = provider?.name ?? t('customer.dashboard.providerFallback');
  const brandName = provider?.name ?? provider?.slug ?? t('customer.dashboard.providerFallback');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [password, setPassword] = useState('');
  const [usePassword, setUsePassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [socialError, setSocialError] = useState<string | null>(null);
  const authenticatedRedirectStartedRef = useRef(false);
  const domainInfo = useMemo(() => getCurrentDomain(), []);
  const resolvedTenantSlug = provider?.slug ?? domainInfo.subdomain ?? undefined;
  const { enabled: passwordLoginEnabled } = usePasswordLoginAvailability('customer', {
    tenantSlug: resolvedTenantSlug,
  });
  const {
    providers: enabledSocialProviders,
    providerStatus,
    isLoading: isSocialConfigLoading,
  } = useAvailableSocialProviders({
    scope: 'customer',
    tenantSlug: resolvedTenantSlug,
  });

  const {
    startLogin: startSocialLogin,
    isLoading: isSocialLoading,
    activeProvider,
    error: socialPluginError,
  } = useSocialAuth({
    scope: 'customer',
    tenantSlug: resolvedTenantSlug,
    loginHint: normalizeEmail(email) || undefined,
    prompt: 'select_account',
    onSuccess: async (payload, response) => {
      setSocialError(null);
      await hydrateFromResponse(payload);
      const redirectUrl = resolveSigninRedirectUrl(response) ?? resolveSigninRedirectUrl(payload);
      if (redirectUrl) {
        window.location.assign(redirectUrl);
        return;
      }
      navigate('/customer', { replace: true });
    },
    onError: (message) => {
      setSocialError(message);
    },
  });

  const visibleSocialProviders = useMemo<OAuthProvider[]>(() => {
    if (enabledSocialProviders.length === 0) {
      return [];
    }

    const enabledSet = new Set(enabledSocialProviders);
    const ordered = SOCIAL_PROVIDER_ORDER.filter((providerId) => enabledSet.has(providerId));
    const extras = enabledSocialProviders.filter(
      (providerId) => !SOCIAL_PROVIDER_ORDER.includes(providerId),
    );

    return [...ordered, ...extras];
  }, [enabledSocialProviders]);

  const providerStatusMap = useMemo(
    () => new Map(providerStatus.map((item) => [item.provider, item])),
    [providerStatus],
  );

  const resolveProviderLabel = (providerId: OAuthProvider) => {
    const statusLabel = providerStatusMap.get(providerId)?.label;
    if (statusLabel) {
      return t('auth.continueWithProvider', 'Continue with {{provider}}', { provider: statusLabel });
    }
    return t(PROVIDER_LABEL_KEYS[providerId], PROVIDER_LABEL_FALLBACK[providerId]);
  };

  const resolveProviderIcon = (providerId: OAuthProvider) => {
    const iconName = providerStatusMap.get(providerId)?.icon?.toLowerCase();
    if (iconName === 'microsoft' || providerId === 'azure') {
      return MicrosoftIcon;
    }
    if (providerId === 'google') {
      return GoogleIcon;
    }
    if (providerId === 'apple') {
      return Apple;
    }
    if (providerId === 'github') {
      return Github;
    }
    return Globe;
  };

  const shouldShowSocialLogins = !isSocialConfigLoading && visibleSocialProviders.length > 0;
  const socialGridClass =
    visibleSocialProviders.length === 1
      ? 'grid-cols-1'
      : visibleSocialProviders.length === 2
        ? 'grid-cols-2'
        : 'grid-cols-3';

  useEffect(() => {
    if (socialPluginError && socialPluginError !== socialError) {
      setSocialError(socialPluginError);
    }
  }, [socialPluginError, socialError]);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      if (authenticatedRedirectStartedRef.current) {
        return;
      }

      authenticatedRedirectStartedRef.current = true;
      let cancelled = false;

      const redirectAuthenticatedCustomer = async () => {
        try {
          const regenerateResponse = await authService.regenerateCustomerSigninRedirectToken();
          if (cancelled) {
            return;
          }

          if (regenerateResponse.signin_redirect_url) {
            window.location.assign(regenerateResponse.signin_redirect_url);
            return;
          }
        } catch (error) {
          if (cancelled) {
            return;
          }

          const status = resolveAuthErrorStatus(error);
          if (status === 401) {
            return;
          }
          if (status === 422) {
            navigate('/customer', { replace: true });
            return;
          }
        }

        try {
          const response = await authService.getCurrentCustomer();
          if (cancelled) {
            return;
          }

          const redirectUrl = resolveSigninRedirectUrl({
            meta: response.meta as Record<string, unknown> | undefined,
          });
          if (redirectUrl) {
            window.location.assign(redirectUrl);
            return;
          }

          navigate('/customer', { replace: true });
        } catch {
          if (!cancelled) {
            navigate('/customer', { replace: true });
          }
        }
      };

      void redirectAuthenticatedCustomer();

      return () => {
        cancelled = true;
      };
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (!passwordLoginEnabled) {
      setUsePassword(false);
    }
  }, [passwordLoginEnabled]);

  const termsUrlQuery = usePublicProviderTermsUrl();
  const termsUrl = termsUrlQuery.data ?? null;

  const requestMagicLink = async () => {
    setError('');
    setPasswordError('');
    setSocialError(null);

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const normalizedEmail = normalizeEmail(email);
    if (!emailPattern.test(normalizedEmail)) {
      setError(t('customer.portal.login.invalidEmail'));
      return;
    }

    setRequesting(true);

    try {
      await authService.sendCustomerOTP({ email: normalizedEmail });
      navigate('/customer/login/verify', { state: { email: normalizedEmail } });
    } catch (err) {
      setError(resolveAuthErrorMessage(err, t('customer.portal.login.sendCodeError'), t));
    } finally {
      setRequesting(false);
    }
  };

  const handleMagicLinkSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await requestMagicLink();
  };

  const handleSocialButtonClick = (providerId: OAuthProvider) => {
    setSocialError(null);
    setPasswordError('');
    if (requesting || isSocialLoading) {
      return;
    }

    void startSocialLogin(providerId);
  };

  const handlePasswordSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setPasswordError('');
    setSocialError(null);

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const normalizedEmail = normalizeEmail(email);
    if (!emailPattern.test(normalizedEmail)) {
      setPasswordError(t('customer.portal.login.invalidEmail'));
      return;
    }

    if (password.trim().length < 8) {
      setPasswordError(t('auth.passwordTooShort'));
      return;
    }

    setPasswordLoading(true);
    try {
      const response = await authService.loginCustomerWithPassword({
        email: normalizedEmail,
        password,
      });
      await hydrateFromResponse(response.data);
      const redirectUrl = resolveSigninRedirectUrl(response);
      if (redirectUrl) {
        window.location.assign(redirectUrl);
        return;
      }
      navigate('/customer', { replace: true });
    } catch (err) {
      const status = resolveAuthErrorStatus(err);
      if (status === 423) {
        setPasswordError(
          resolveAuthErrorMessage(
            err,
            t(
              'customer.portal.login.passwordLoginDisabled',
              'Password login is disabled for this customer portal.',
            ),
            t,
          ),
        );
      } else {
        setPasswordError(
          resolveAuthErrorMessage(
            err,
            t('customer.portal.login.invalidCredentials', 'Invalid email or password.'),
            t,
          ),
        );
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background px-4">
      <div className="absolute right-4 top-4">
        <LanguageSwitcher />
      </div>

      <div className="flex w-full max-w-md flex-col items-center">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          {brandLogo ? (
            <img
              src={brandLogo}
              alt={`${brandName} logo`}
              className="h-10 w-auto max-w-[200px] object-contain"
            />
          ) : (
            <span className="text-xl font-semibold text-foreground">{brandName}</span>
          )}
        </div>

        <Card className="w-full">
          <CardHeader className="space-y-2 text-center">
            <CardTitle>{t('customer.portal.login.title')}</CardTitle>
            <CardDescription>
              {t(
                usePassword
                  ? 'customer.portal.login.passwordDescription'
                  : 'customer.portal.login.description',
                {
                  provider: providerName,
                  defaultValue: usePassword
                    ? 'Enter the email associated with your anymize.ai subscription. We will send a 6-digit code to verify your identity.'
                    : t('customer.portal.login.description', { provider: providerName }),
                },
              )}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <form
              onSubmit={
                passwordLoginEnabled && usePassword ? handlePasswordSubmit : handleMagicLinkSubmit
              }
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="customer-email">{t('customer.portal.login.emailLabel')}</Label>
                <Input
                  id="customer-email"
                  type="email"
                  placeholder={t('customer.portal.login.emailPlaceholder')}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  disabled={requesting || passwordLoading}
                />
              </div>

            {passwordLoginEnabled && usePassword ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="customer-password">
                    {t('customer.portal.login.passwordLabel', 'Password')}
                  </Label>
                  <Input
                    id="customer-password"
                    type="password"
                    placeholder={t('customer.portal.login.passwordPlaceholder', 'Your password')}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    disabled={passwordLoading || requesting}
                  />
                </div>
              </>
            ) : null}

            {passwordLoginEnabled && usePassword ? (
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 text-xs"
                  onClick={() => navigate('/customer/forgot-password')}
                  disabled={passwordLoading || requesting}
                >
                  {t('customer.portal.login.forgotPassword')}
                </Button>
              </div>
            ) : null}

            {passwordLoginEnabled && usePassword && passwordError ? (
              <p className="text-sm text-destructive">{passwordError}</p>
            ) : null}
            {!usePassword && error ? (
              <p className="text-sm text-destructive">{error}</p>
            ) : null}

            <Button
              type="submit"
              className="w-full"
              disabled={passwordLoading || requesting}
            >
              {passwordLoginEnabled && usePassword ? (
                passwordLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('customer.portal.login.passwordSubmitting', 'Signing in…')}
                  </>
                ) : (
                  <>
                    <Lock className="mr-2 h-4 w-4" />
                    {t('customer.portal.login.passwordSubmit', 'Sign in with password')}
                  </>
                )
              ) : requesting ? (
                t('customer.portal.login.submitting')
              ) : (
                t('customer.portal.login.submit')
              )}
            </Button>
          </form>

          {passwordLoginEnabled ? (
            <div className="space-y-3 pt-1">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    {t('common.or', 'OR')}
                  </span>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => {
                  setUsePassword((prev) => !prev);
                  setError('');
                  setPasswordError('');
                }}
                disabled={requesting || passwordLoading}
              >
                {usePassword
                  ? t('customer.portal.login.useSignInCode', 'Use sign-in code')
                  : t('customer.portal.login.usePassword', 'Use password')}
              </Button>
            </div>
          ) : null}

          {socialError ? <p className="mt-2 text-sm text-destructive">{socialError}</p> : null}

          {shouldShowSocialLogins ? (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <Separator />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    {t('auth.orContinueWith')}
                  </span>
                </div>
              </div>

              <div className={`grid gap-3 ${socialGridClass}`}>
                {visibleSocialProviders.map((providerId) => {
                  const ProviderIcon = resolveProviderIcon(providerId);
                  return (
                    <Button
                      key={providerId}
                      variant="outline"
                      onClick={() => handleSocialButtonClick(providerId)}
                      disabled={requesting || isSocialLoading}
                      aria-label={resolveProviderLabel(providerId)}
                    >
                      {isSocialLoading && activeProvider === providerId ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <ProviderIcon className="h-5 w-5" aria-hidden="true" />
                      )}
                    </Button>
                  );
                })}
              </div>
            </>
            ) : null}
          </CardContent>

          <CardFooter className="flex flex-col gap-2 text-center text-xs text-muted-foreground">
            <p>
              {t('customer.portal.login.registerPrompt')}{' '}
              <button
                type="button"
                className="text-primary underline"
                onClick={() => navigate('/customer/register')}
              >
                {t('customer.portal.login.registerCta')}
              </button>
            </p>
            <p>{t('customer.portal.login.tip')}</p>
            {termsUrl ? (
              <p>
                <a
                  href={termsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline"
                >
                  {t('common.termsOfService')}
                </a>
              </p>
            ) : null}
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
